import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { getThLegacyCognitoIdFromClaims } from 'microsites/partner-linking/app-token';

import { userHasSignedIn as userHasSignedInOnCognito } from 'remote/auth';
import { IAdobeEvent, TGTMDataLayer } from 'state/adobe/adobe-analytics-adapter/types';
import { initDatadogFlags } from 'state/datadog-flags';
import { initCognitoSession } from 'utils/cognito';
import { RBIEnv, brand, env, isLocalDev, platform } from 'utils/environment';
import { initLaunchDarkly } from 'utils/launchdarkly';

declare global {
  interface Window {
    dataLayer: TGTMDataLayer;
    adobeDataLayer: IAdobeEvent<Record<string, any>>[];
    LOADING_START_TIME: number;
    /**
     * window.Cypress exists when our app is running within Cypress
     * Places we currently check for this:
     * - frontend/src/state/graphql/links/index.ts - avoid BatchHttpLink
     *
     * https://docs.cypress.io/faq/questions/using-cypress-faq.html#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
     */
    Cypress?: any;
    // When run with cypress for cypress-v2 package,
    // we instantiate the LD client with predefined flags
    _initial_cypress_feature_flags?: object;
    // Skip the interval for checking for unavailable items.
    // Avoids race condition in cart with recorded tests
    _skipUnavailableItemsIntervalCheck?: boolean;
  }
}

const emitLoadingSuccessEvent = (rbiBrand: string, rbiPlatform: string) => {
  const successTime = performance.now?.();
  const durationTillSuccess = successTime - window.LOADING_START_TIME;
  (window.dataLayer || []).push({
    event: 'APP_BUNDLE_LOADING_SUCCESS',
    brand: rbiBrand,
    timestamp: successTime,
    duration: durationTillSuccess,
    platform: rbiPlatform,
  });
};

export default async function bootstrap(
  renderApp: ({
    flags,
    userHasSignedIn,
    userSession,
  }: {
    flags: Record<string, unknown>;
    userHasSignedIn: boolean;
    userSession: CognitoUserSession | null;
  }) => void
): Promise<void> {
  // Ensure dataLayer exists so events logged before GTM injection are preserved.
  window.dataLayer = window.dataLayer || [];

  const [ENV, BRAND, PLATFORM] = [env(), brand().toUpperCase(), platform()];

  if (!isLocalDev) {
    emitLoadingSuccessEvent(BRAND, PLATFORM);
  }

  // Stood up alongside LaunchDarkly, not instead of it: the flags already in
  // `utils/launchdarkly` keep evaluating exactly as before. Only new flags are
  // authored against this provider.
  //
  // Chained onto the Cognito session rather than awaited after it, for two
  // reasons that pull in opposite directions. It has to come *after* the
  // session, because the account identity is what a signed-in guest is
  // bucketed on, and asking earlier would fetch a configuration for an
  // anonymous guest and correct it a moment later — a visible flicker on
  // anything a flag controls above the fold. But it must not *extend* boot:
  // this render is gated on the whole block, so awaiting it serially would
  // put Datadog's worst case on the critical path to first paint. Inside the
  // Promise.all it overlaps LaunchDarkly instead.
  const [flags, userSession] = await Promise.all([
    initLaunchDarkly(),
    initCognitoSession().then(async session => {
      // Read defensively. `initDatadogFlags` is deliberately non-fatal, but
      // that only protects the call — this expression is evaluated before it,
      // so an unexpected session shape would throw here, outside any handler,
      // and boot would stop. Flags are optional; rendering the app is not.
      const claims = session?.getIdToken?.()?.payload;

      await initDatadogFlags({
        cognitoId: claims?.sub,
        // The loyalty id is what the app buckets rollouts on, so it has to be
        // in the very first evaluation rather than arriving with the sign-in
        // refresh — a guest already signed in at boot never takes that path.
        legacyCognitoId: getThLegacyCognitoIdFromClaims(claims),
      });
      return session;
    }),
  ]);

  // this has to be after init cognito session.
  const userHasSignedIn = await userHasSignedInOnCognito();

  if (ENV === RBIEnv.TEST) {
    const defaultFlags = {};
    renderApp({
      flags: defaultFlags,
      userHasSignedIn,
      userSession,
    });
    return;
  }

  renderApp({
    flags,
    userHasSignedIn,
    userSession,
  });
}
